// client/src/contexts/SyncContext.jsx
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';

const SyncContext = createContext();

export const useSync = () => {
  const context = useContext(SyncContext);
  if (!context) {
    throw new Error('useSync must be used within a SyncProvider');
  }
  return context;
};

export const SyncProvider = ({ children }) => {
  const { isAuthenticated, logout } = useAuth();
  const [lastChange, setLastChange] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const connect = () => {
    if (!isAuthenticated) return;
    
    // Cleanup previous connection if any
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const token = sessionStorage.getItem('token');
    const baseUrl = window.location.origin.includes('localhost') 
      ? 'http://localhost:5011' 
      : window.location.origin.replace(':3011', ':5011');
      
    const url = `${baseUrl}/api/sync/stream?token=${token}`;
    
    console.log(`[Sync] Establishing clinical real-time link: ${url}`);
    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.addEventListener('handshake', (event) => {
      try {
        const { serverInstanceId } = JSON.parse(event.data);
        const storedId = sessionStorage.getItem('serverInstanceId');
        
        console.log('[Sync] Received Hub handshake:', serverInstanceId);
        
        if (storedId && storedId !== serverInstanceId) {
          console.warn('[Sync] Server restart detected. Workstation session invalidated for data integrity.');
          logout();
        } else if (!storedId) {
          sessionStorage.setItem('serverInstanceId', serverInstanceId);
        }
      } catch (err) {
        console.error('[Sync] Failed to parse clinical handshake:', err);
      }
    });

    es.addEventListener('db-change', (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[Sync] Surgical record update detected:', data);
        setLastChange({ ...data, timestamp: Date.now() });
      } catch (err) {
        console.error('[Sync] Failed to parse clinical update:', err);
      }
    });

    es.onerror = (err) => {
      console.error('[Sync] Clinical stream error, reconnecting...', err);
      setIsConnected(false);
      es.close();
      
      // Professional exponential backoff for medical network stability
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = setTimeout(connect, 5000);
    };

    es.onopen = () => {
      console.log('[Sync] Clinical real-time link established.');
      setIsConnected(true);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  };

  useEffect(() => {
    if (isAuthenticated) {
      connect();
    } else {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    }

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [isAuthenticated]);

  return (
    <SyncContext.Provider value={{ lastChange, isConnected }}>
      {children}
    </SyncContext.Provider>
  );
};
